// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { setFilterDrawerIsOpen } from "@conklin/App/Handlers/XNG/XListFilter";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import Button, { ButtonPresentationProps } from "@conklin/MobiusOverrides/Button/Button";
import { LinkPresentationProps } from "@conklin/MobiusOverrides/Link/Link";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext, HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    // XNG-Changes
    const pagination = getProductListDataViewProperty(state, "pagination");

    return {
        pageSize: pagination?.pageSize,
        totalItemCount: pagination?.totalItemCount,
        // XNG-Change
        isTabletScreen: state.xCommon.isTabletScreen,
    };
};

type Props = ReturnType<typeof mapStateToProps> & WidgetProps & HasCategoryContext;

export interface ProductListControlsContainerStyles {
    container?: GridContainerProps;
    countGridItem?: GridItemProps;
    countTableViewGridItem?: GridItemProps;
    sortGridItem?: GridItemProps;
    sortTableViewGridItem?: GridItemProps;
    filterGridItem?: GridItemProps;
    filterTableViewGridItem?: GridItemProps;
    viewSelectGridItem?: GridItemProps;
    viewSelectTableViewGridItem?: GridItemProps;
    // XNG-Changes
    filterBtn?: LinkPresentationProps;
    leftColumn?: InjectableCss;
    rightColumn?: InjectableCss;
    tabletContainer?: InjectableCss;
}

export const controlsContainerStyles: ProductListControlsContainerStyles = {
    container: {
        gap: 0,
    },
    countGridItem: {
        width: [12, 12, 2, 5, 6],
    },
    countTableViewGridItem: {
        width: [12, 12, 1, 1, 1],
    },
    sortGridItem: {
        width: [12, 12, 5, 4, 4],
    },
    sortTableViewGridItem: {
        width: [12, 12, 5, 4, 3],
    },
    filterGridItem: {
        width: 0,
    },
    filterTableViewGridItem: {
        width: [0, 0, 2, 2, 2],
    },
    viewSelectGridItem: {
        width: [0, 0, 5, 3, 2],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    viewSelectTableViewGridItem: {
        width: [0, 0, 4, 5, 6],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = controlsContainerStyles;

const ProductListControlsContainer: FC<Props> = ({
    id,
    // view,
    // XNG-Change
    isTabletScreen,
}) => {
    // XNG-Changes
    const dispatch = useAppDispatch();
    const handleOpenFilterDrawer = () => {
        dispatch({
            type: "xProductList/FilterDrawer/SetFilterDrawerIsOpen",
            filterDrawerIsOpen: true,
        });
    };

    if (isTabletScreen) {
        return (
            <StyledWrapper {...styles.tabletContainer}>
                <Link
                    aria-label="open filter drawer"
                    data-test-selector="filter-drawer-trigger"
                    {...styles.filterBtn}
                    onClick={handleOpenFilterDrawer}
                >
                    {translate("Filter Results")}
                </Link>
                <StyledWrapper className="pagination-page-number">
                    <Zone contentId={id} zoneName="Content03" />
                </StyledWrapper>
            </StyledWrapper>
        );
    }
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.leftColumn}>
                <StyledWrapper>
                    <Zone contentId={id} zoneName="Content00" />
                </StyledWrapper>
                <StyledWrapper className="hide-on-tablet">
                    <Zone contentId={id} zoneName="Content01" />
                </StyledWrapper>
                <StyledWrapper className="hide-on-tablet">
                    <Zone contentId={id} zoneName="Content02" />
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.rightColumn}>
                <StyledWrapper className="pagination-page-number">
                    <Zone contentId={id} zoneName="Content03" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(ProductListControlsContainer)),
    definition: {
        group: "Product List",
        displayName: "List Controls Container",
        // XNG-changes: add CategoryDetailsPage
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
